import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "", organization_name: "", email: "", password: "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Workspace created");
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <div className="overline text-accent mb-2 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span>/ FOUNDING ACCESS</span>
          </div>
          <h1 className="font-display text-3xl font-bold">Claim your founding spot.</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Free during beta · 50% off for life at launch · No credit card.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="fn">Full name</Label>
              <Input id="fn" required data-testid="register-fullname"
                className="rounded-sm mt-1.5"
                value={form.full_name} onChange={update("full_name")} />
            </div>
            <div>
              <Label htmlFor="org">Organization name</Label>
              <Input id="org" required data-testid="register-org"
                className="rounded-sm mt-1.5"
                value={form.organization_name} onChange={update("organization_name")} />
            </div>
            <div>
              <Label htmlFor="em">Work email</Label>
              <Input id="em" type="email" required data-testid="register-email"
                className="rounded-sm mt-1.5"
                value={form.email} onChange={update("email")} />
            </div>
            <div>
              <Label htmlFor="pw">Password</Label>
              <Input id="pw" type="password" required minLength={6}
                data-testid="register-password"
                className="rounded-sm mt-1.5"
                value={form.password} onChange={update("password")} />
            </div>
            <Button type="submit" disabled={busy}
              data-testid="register-submit"
              className="w-full rounded-sm bg-primary hover:bg-primary/90 h-11">
              {busy ? "Creating…" : "Create workspace"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-muted-foreground">
            Have an account?{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block border-l border-border bg-zinc-950 text-white relative overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="font-display font-black text-2xl tracking-tight self-end">
            Rule<span className="text-primary">AI</span><span className="text-zinc-500">.systems</span>
          </Link>
          <div>
            <div className="overline text-accent mb-4">/ FOUNDING BENEFITS</div>
            <ul className="space-y-3 text-sm text-zinc-300 font-mono-plex">
              <li>◆ Free during beta</li>
              <li>◆ 50% off for life at launch</li>
              <li>◆ Founding Member badge</li>
              <li>◆ Direct roadmap influence</li>
              <li>◆ All 15 modules unlocked</li>
              <li>◆ AI drafting (Claude Sonnet 4.6)</li>
              <li>◆ 10 spreadsheet templates</li>
            </ul>
          </div>
          <div className="overline text-zinc-500">◉ LIMITED FOUNDING ACCOUNTS</div>
        </div>
      </div>
    </div>
  );
}
